import { useNavigate } from "react-router-dom";
import { useLoans, useSuretyRequests, useSuretySignoff } from "../../hooks/useLoans";
import Card from "../../components/ui/Card";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import StatCard from "../../components/ui/StatCard";
import { useAuth } from "../../context/AuthContext";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", currencyDisplay: "symbol", maximumFractionDigits: 0 }).format(n);
const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const statusColor = (s: string): any =>
  s === "active" || s === "disbursed" ? "emerald" : s === "repaid" ? "blue" : s === "overdue" ? "red" : "amber";

export default function LoansPage() {
  const { user }                       = useAuth();
  const navigate                       = useNavigate();
  const { data: loans       = [] }     = useLoans()          as any;
  const { data: suretyReqs  = [] }     = useSuretyRequests() as any;
  const signoff                        = useSuretySignoff();

  const today = new Date();
  const dateJoined = new Date(user?.created_at.toString() || "");
  const dateDiff = today.getTime() - dateJoined.getTime();
  const sixMonths = 182.5 * 24 * 60 * 60 * 1000;

  const loanApplication = ['pending-surety','approved'];

  const pending = (suretyReqs as any[]).filter((r: any) => r.status === "pending");

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-slate-500">Your loans and surety obligations</p>
        {user?.kyc_status != "verified"? "" : dateDiff >= sixMonths?
        <Button size="sm" onClick={() => navigate("/loans/apply")}>+ Apply for Loan</Button> : ""
        }
      </div>

      {/* Surety queue */}
      {pending.length > 0 && (
        <Card className="mb-4 border-amber-400 bg-amber-50">
          <p className="text-sm font-medium text-amber-800 mb-3">⚠️ Surety sign-off required</p>
          {pending.map((req: any) => {
            const loanReq  = Number(req.amount) * 0.5;
            const eligible = true; // balance check done server-side
            return (
              <div key={req.id} className="bg-white rounded-lg p-3.5 mb-2 last:mb-0 border border-amber-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{req.borrower_name} requests your surety</p>
                    <p className="text-xs text-slate-500 mt-0.5">Loan: {fmt(req.amount)} · Your required balance: {fmt(loanReq)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Purpose: {req.purpose}</p>
                    <em className="text-xs"><b>Please Note:</b> It is unsafe to guarantee someone you don't know. 
                      When you agree to guarantee this member, you agree to indemnify the cooperative to the tune of {fmt(loanReq)} and  
                      this value will be locked in your account until the loan is fully paid. If {req.borrower_name} fails to pay the loan, 
                      this amount will be recovered from you.</em>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0 ml-3">
                    <Button size="sm" variant="danger"
                      onClick={() => signoff.mutate({ loanId: req.loan_id, decision: "declined" })}
                      disabled={signoff.isPending}>
                      Decline
                    </Button>
                    <Button size="sm"
                      onClick={() => signoff.mutate({ loanId: req.loan_id, decision: "approved" })}
                      disabled={signoff.isPending || !eligible}>
                      ✍️ Sign off
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {/* Loans list */}
      {(loans as any[]).length === 0 ? (
        <Card className="text-center py-12">
          <span className="text-4xl">🤝</span>
          <p className="text-sm text-slate-500 mt-3">No loans yet</p>
          {user?.kyc_status != "verified"? "" : 
          <Button size="sm" className="mt-3" onClick={() => navigate("/loans/apply")}>Apply for your first loan</Button>
          }
        </Card>
      ) : (
        (loans as any[]).map((loan: any) => {
          const repaidPct = loan.amount_repaid ? Math.round((loan.amount_repaid / loan.total_amount) * 100) : 0;
          return (
            <Card key={loan.id} className="mb-3">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-xl font-medium text-slate-800">{loanApplication.includes(loan.status)? fmt(loan.amount) : fmt(loan.total_amount)}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{loan.purpose}</p>
                </div>
                <Badge label={loan.status} color={statusColor(loan.status)}/>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div><p className="text-[10px] text-slate-400 mb-0.5">Disbursed</p><p className="text-xs text-slate-700">{fmtDate(loan.disbursed_at)}</p></div>
                <div><p className="text-[10px] text-slate-400 mb-0.5">Due date</p><p className="text-xs text-slate-700">{fmtDate(loan.due_at)}</p></div>
                <div><p className="text-[10px] text-slate-400 mb-0.5">Repaid</p><p className="text-xs text-emerald-600 font-medium">{fmt(loan.amount_repaid ?? 0)}</p></div>
                <div><p className="text-[10px] text-slate-400 mb-0.5">Balance</p><p className="text-xs text-emerald-600 font-medium">{loan.status==="disbursed"? fmt((loan.total_amount ?? 0) - (loan.amount_repaid ?? 0)) : 0}</p></div>
              </div>
              <div className="mb-3">
                <div className="flex justify-between text-xs text-slate-400 mb-1"><span>Repayment progress</span><span>{repaidPct}%</span></div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${repaidPct}%` }}/>
                </div>
              </div>
              {loan.sureties?.filter((s: any) => s.surety_id).length > 0 && (
                <div>
                  <p className="text-xs text-slate-400 mb-1.5">Sureties</p>
                  <div className="flex gap-2 flex-wrap">
                    {loan.sureties.filter((s: any) => s.surety_id).map((s: any) => (
                      <div key={s.surety_id} className="flex items-center gap-1.5 bg-emerald-50 rounded-lg px-3 py-1.5 border border-emerald-200">
                        <span className="text-xs font-medium text-emerald-800">{s.name}</span>
                        <Badge label={s.status === "approved" ? "✅ Signed" : "⏳ Pending"} color={s.status === "approved" ? "emerald" : "amber"}/>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
}
